import React, { useCallback, useMemo } from 'react';
import { Box, Tabs, Tab } from 'extended-oxygen-elements';
import { DAYS_OF_WEEK, DayOfWeek } from '../types';

// Inline constants to avoid module resolution issues
const COLORS = {
  primary: '#000000',
  secondary: '#545454',
  scrollButtonDisabled: '#9e9e9e',
} as const;

type LocationTabsProps = {
  selectedDay: DayOfWeek;
  onDayChange: (day: DayOfWeek) => void;
};

export function LocationTabs({ selectedDay, onDayChange }: LocationTabsProps) {
  // Memoize the selected index to avoid recalculating
  const selectedIndex = useMemo(
    () => DAYS_OF_WEEK.indexOf(selectedDay),
    [selectedDay]
  );

  // Memoize the change handler
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      onDayChange(DAYS_OF_WEEK[newValue]);
    },
    [onDayChange]
  );

  return (
    <Box 
      sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        mb: 2,
      }}
    >
      <Tabs
        value={selectedIndex}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Select day of week to view duties"
        sx={{
          minHeight: '40px',
          '& .MuiTab-root': {
            minHeight: '40px',
            textTransform: 'none',
            fontWeight: 400,
            fontSize: '15px',
            color: COLORS.primary,
            '&.Mui-selected': {
              fontWeight: 600,
              color: 'primary.main',
            },
            '@media (max-width: 600px)': {
              minWidth: '48px',
              padding: '8px 12px',
              fontSize: '14px',
            },
          },
          '& .MuiTabs-scrollButtons': {
            color: COLORS.secondary,
            '&.Mui-disabled': {
              opacity: 0.3,
              color: COLORS.scrollButtonDisabled,
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: 'primary.main', // Visible indicator
          },
        }}
      >
        {DAYS_OF_WEEK.map((day) => (
          <Tab 
            key={day} 
            label={day} 
            id={`day-tab-${day}`}
            aria-controls={`day-panel-${day}`}
          />
        ))}
      </Tabs>
    </Box>
  );
}

export default LocationTabs;
